import React, { useState, useEffect } from 'react'
import { connect as connectRedux } from 'react-redux'
import { useLazyQuery } from '@apollo/client'
import { fromJS, Map, List } from 'immutable'
import _get from 'lodash/get'
import { getBoundActions } from '../../actions/index'
import {
  createTitleGroupId,
  getFormattedContentData,
  getLocationType,
  CONTENT_IMAGE_LIST,
  CONTENT_IMAGE_TYPE_HERO_WITH_TEXT,
} from '../../services/content/index'
import { getPrimary } from '../../services/languages/index'
import { GET_CONTENT } from '../../services/content/query'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../services/apollo-graphql/index'
import Sherpa from '../Sherpa/index'
import {
  DETAIL_TYPE_SERIES_NORMAL,
  DETAIL_TYPE_SERIES_YOGA,
  DETAIL_TYPE_COURSE,
  getDetailType,
  DETAIL_TYPE_SERIES_FITNESS,
  DETAIL_TYPE_EPISODE_NORMAL,
  DETAIL_TYPE_EPISODE_YOGA,
  DETAIL_TYPE_EPISODE_FITNESS,
  DETAIL_TYPE_EPISODE_GUIDE,
  getDetailPageUiLocation,
} from './utils'
import DetailPageV3Video from './DetailPageV3Video'
import DetailPageV3Series from './DetailPageV3Series'
import DetailPageV3Course from './DetailPageV3Course/DetailPageV3Course'
import DetailPageV3Episode from './DetailPageV3Episode'

function DetailPageV3 (props) {
  const {
    auth,
    user,
    resolver,
    location,
    history,
    apolloClient,
  } = props

  const jwt = auth.get('jwt')
  const userLanguage = getPrimary(user.getIn(['data', 'language'], List()))
  const locationPath = _get(location, 'pathname')
  const contentId = resolver.getIn(['data', 'id'])
  const resolverPath = resolver.getIn(['data', 'path'])
  const contentType = resolver.getIn(['data', 'content_type'])

  const [getDetailData, { data, loading }] = useLazyQuery(GET_CONTENT, {
    // Default is 'none': Apollo drops `data` when the response includes GraphQL errors.
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
  })

  const [detailId, setDetailId] = useState(contentId)
  const [detailData, setDetailData] = useState()
  const [detailType, setDetailType] = useState()
  const [detailAuth, setDetailAuth] = useState(auth || Map())
  const [uiLocation, setUiLocation] = useState(null)

  const locationIsSynced = locationPath === resolverPath
  const detailIsSynced = contentId === detailId && locationIsSynced
  const detailDataIsReady = detailData && detailData.size > 0 && detailData.get('contentId') === contentId
  const titleGroupId = detailId && contentType ? createTitleGroupId(detailId, contentType) : null
  const detailAuthUid = detailAuth.get('uid')
  const authUid = auth.get('uid')

  useEffect(() => {
    if (titleGroupId && detailIsSynced) {
      getDetailData({
        variables: {
          language: [userLanguage, '_primary'],
          primaryLanguage: [userLanguage],
          titleGroupIds: [titleGroupId],
          imageType: [...CONTENT_IMAGE_LIST, CONTENT_IMAGE_TYPE_HERO_WITH_TEXT],
          limitedData: false,
          includeSeriesImage: true,
          includeReferenceImage: true,
          learnerLevelLanguage: userLanguage,
        },
        fetchPolicy: detailAuthUid !== authUid
          ? APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK
          : APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
        context: {
          jwt,
        },
      })
    }
  }, [userLanguage, titleGroupId, jwt, detailIsSynced, detailAuthUid, authUid])

  useEffect(() => {
    if (!loading && data) {
      const content = fromJS(_get(data, ['contentList', 'content', 0], {}))
      const formattedData = fromJS(getFormattedContentData(content)) || Map()
      setDetailType(getDetailType(formattedData))
      setDetailData(formattedData)
      setUiLocation(getDetailPageUiLocation(getLocationType({ data: formattedData })))
    }
  }, [data, loading])

  useEffect(() => {
    if (contentId) {
      setDetailId(contentId)
    }
  }, [contentId])

  // set the user's auth for this page to help handle loading detail data
  // if the user's auth changes (renewal, login, etc.), then load detail data again
  useEffect(() => {
    if (detailAuthUid !== authUid) {
      setDetailAuth(auth)
    }
  }, [auth, detailAuthUid, authUid])

  // TODO replace with a placeholder once it is designed
  if (loading || !detailDataIsReady || !locationIsSynced) {
    return (
      <div className="detail-page-v3">
        <div className="detail-page-v3__loading">
          <Sherpa />
        </div>
      </div>
    )
  }

  /**
   * TODO update when all details templates are implemented
   */
  switch (detailType) {
    case DETAIL_TYPE_COURSE:
      return <DetailPageV3Course detailData={detailData} />
    case DETAIL_TYPE_SERIES_NORMAL:
    case DETAIL_TYPE_SERIES_YOGA:
    case DETAIL_TYPE_SERIES_FITNESS:
      return <DetailPageV3Series
        detailData={detailData}
        location={location}
        history={history}
        apolloClient={apolloClient}
        uiLocation={uiLocation}
      />
    case DETAIL_TYPE_EPISODE_NORMAL:
    case DETAIL_TYPE_EPISODE_YOGA:
    case DETAIL_TYPE_EPISODE_FITNESS:
    case DETAIL_TYPE_EPISODE_GUIDE:
      return <DetailPageV3Episode
        detailData={detailData}
        location={location}
        history={history}
        apolloClient={apolloClient}
        uiLocation={uiLocation}
      />
    default:
      return <DetailPageV3Video
        detailData={detailData}
        location={location}
        history={history}
        apolloClient={apolloClient}
        uiLocation={uiLocation}
      />
  }
}

export default connectRedux((state) => ({
  auth: state.auth,
  user: state.user,
  resolver: state.resolver,
  staticText: state.staticText.getIn(['data', 'detailPageV3', 'data'], Map()),
}), (dispatch) => {
  const actions = getBoundActions(dispatch)
  return {
    setEventDefault: actions.eventTracking.setEventDefault,
  }
})(DetailPageV3)
